import { setPortalMuted, reloadUserMuted } from "./sound";
import { applyPlatformLang } from "./i18n";
import { loadBridgeStore, enableCrazyGamesData } from "./store";
// Builds for game portals (e.g. CrazyGames) are made with REACT_APP_PORTAL=crazygames.
// The normal website build leaves this empty and behaves exactly as before.
export const PORTAL = process.env.REACT_APP_PORTAL || "";
export const isCrazyGames = PORTAL === "crazygames";
export const isGameDistribution = PORTAL === "gamedistribution";
export const isPlaygama = PORTAL === "playgama";
export const isGamePix = PORTAL === "gamepix";
export const isY8 = PORTAL === "y8";
export const isDiscordBuild = PORTAL === "discord";
// itch.io: a plain HTML5 upload in itch's iframe. No portal SDK and no ads; online play works.
export const isItch = PORTAL === "itch";
// Web game portals that show their own ads between games.
export const isPortal = isCrazyGames || isGameDistribution || isPlaygama || isGamePix || isY8;
// Portals where the game runs entirely in the browser: no Firebase, no outside connections apart
// from the portal's own SDK (Playgama's sandbox blocks them; GamePix allows no third-party SDKs).
// Computer games only. Quick Play deals a three-round match there (firstGame.ts).
// fb.ts and index.tsx repeat this test inline on process.env so webpack can drop Firebase.
export const isOfflinePortal = isPlaygama || isGamePix || isY8;
// Builds that run inside another site or app: no links out, no outside ads or cookie banner, hash routing.
export const isEmbedded = isPortal || isDiscordBuild || isItch;

// Go to the start screen. Portal builds use hash routing inside an iframe, so a plain "/" would leave the game.
export const goHome = () => {
  if (isEmbedded) window.location.hash = "#/";
  else window.location.href = "/";
};

const sdk = (): any => (window as any).CrazyGames?.SDK;
const gd = (): any => (window as any).gdsdk;
const bridge = (): any => (window as any).bridge;
const gpx = (): any => (window as any).GamePix;
let y8Sdk: any = null;

// Y8: the App ID and Game ID from the game's "SDK Initialization" page in the Y8 Developer Portal,
// given at build time (REACT_APP_Y8_APP_ID, REACT_APP_Y8_GAME_ID; see tools/build-portal.sh).
// GamePix needs no ID in the game: its SDK knows the game from the page it runs in.
export const Y8_APP_ID = process.env.REACT_APP_Y8_APP_ID || "";
export const Y8_GAME_ID = process.env.REACT_APP_Y8_GAME_ID || "";

// Invite links on portals. A portal allows a link out of the game only when it points at the
// portal's own page for the game, so the room code travels as a parameter on that page's URL
// and the game reads it back on load (portalInviteRoom). CrazyGames builds the link itself;
// other portals take the game's page from REACT_APP_PORTAL_GAME_URL (Playgama reads it back as
// bridge.platform.payload, so the parameter is "payload" there). Without a page to point at,
// the lobby falls back to the room code, which "Join a room" on the start screen accepts.
const INVITE_PARAM = "room";
const ROOM_CODE = /^[a-z0-9]{4}$/i;
export const portalGameUrl = process.env.REACT_APP_PORTAL_GAME_URL || "";
export const portalInviteUrl = (roomCode: string): string => {
  if (!isPortal || !roomCode) return "";
  if (isCrazyGames) {
    try {
      const link = sdk()?.game?.inviteLink?.({ [INVITE_PARAM]: roomCode });
      if (typeof link === "string" && link) return link;
    } catch (e) {}
  }
  if (portalGameUrl) {
    try {
      const url = new URL(portalGameUrl);
      url.searchParams.set(isPlaygama ? "payload" : INVITE_PARAM, roomCode);
      return url.toString();
    } catch (e) {}
  }
  return "";
};

// The room code an invite link brought this player here with, or "".
export const portalInviteRoom = (): string => {
  if (!isPortal) return "";
  let code = "";
  try {
    if (isCrazyGames) code = sdk()?.game?.getInviteParam?.(INVITE_PARAM) || "";
  } catch (e) {}
  try {
    if (!code && isPlaygama) code = bridge()?.platform?.payload || "";
  } catch (e) {}
  if (!code) {
    // The parameter on the game's own URL (a portal that passes the page's query through), or
    // on the hash route (#/?room=abcd), which is where a late-arriving payload is put.
    const search = new URLSearchParams(window.location.search);
    const hashQuery = window.location.hash.split("?")[1] || "";
    const hash = new URLSearchParams(hashQuery);
    code = search.get(INVITE_PARAM) || search.get("payload") || hash.get(INVITE_PARAM) || "";
  }
  code = String(code || "").trim().toLowerCase();
  return ROOM_CODE.test(code) ? code : "";
};

// A payload that only becomes readable once the portal's SDK is up: put it on the hash route so
// the start screen (already on screen) re-renders with the "Join game" button.
const announceInviteRoom = () => {
  const code = portalInviteRoom();
  if (!code) return;
  const hashQuery = window.location.hash.split("?")[1] || "";
  if (new URLSearchParams(hashQuery).get(INVITE_PARAM) === code) return;
  const path = (window.location.hash.replace(/^#/, "").split("?")[0] || "/");
  if (path === "/") window.location.hash = `#/?${INVITE_PARAM}=${code}`;
};

// Sound: muted when CrazyGames' own mute setting is on, and always while an ad plays (or, on
// GamePix, whenever its Player pauses the game).
let portalMuted = false;
let adPlaying = false;
const applyMute = () => setPortalMuted(portalMuted || adPlaying);
let ready: Promise<void> | null = null;

// CrazyGames SDK v3 (script tag added to index.html by tools/make-crazygames.js).
export const initPortal = () => {
  if (!isPortal || ready) return;
  if (isGamePix) {
    // GamePix SDK v3, manual lifecycle (window.GamePixConfig and the script tag are added to
    // index.html by tools/make-crazygames.js, before the game's own code). The Player pauses the
    // game for ads and its own overlays: pause must mute, resume restores the game's own setting.
    ready = (async () => {
      try {
        const g = gpx();
        g?.events?.on?.("pause", () => {
          adPlaying = true;
          applyMute();
        });
        g?.events?.on?.("resume", () => {
          adPlaying = false;
          applyMute();
        });
        g?.lifecycle?.loading?.(50);
        applyPlatformLang(g?.lang?.());
      } catch (e) {}
    })();
    return;
  }
  if (isY8) {
    // Y8 SDK (loaded async from index.html): initialise once on "y8sdk.ready"; emitReadyEvent()
    // covers the SDK having loaded first. No player login (autoLogin off: the game has no accounts
    // there). Never waits more than 5 seconds: the game must work without the SDK.
    ready = new Promise<void>((resolve) => {
      const start = () => {
        try {
          const y = (window as any).y8?.sdk?.();
          if (y && !y8Sdk) {
            y8Sdk = y;
            y.init({ appId: Y8_APP_ID, autoLogin: false }, { gameId: Y8_GAME_ID, preloadAdBreaks: "on", sound: "on", onReady: () => undefined });
          }
        } catch (e) {}
        resolve();
      };
      window.addEventListener("y8sdk.ready", start, { once: true });
      try {
        (window as any).y8?.emitReadyEvent?.();
      } catch (e) {}
      setTimeout(resolve, 5000);
    });
    return;
  }
  if (isGameDistribution) {
    // GameDistribution pauses the game (and wants sound off) while its ads play.
    ready = Promise.resolve();
    (window as any).GD_OPTIONS = {
      gameId: process.env.REACT_APP_GD_GAME_ID || "",
      onEvent: (event: any) => {
        if (event?.name === "SDK_GAME_PAUSE") {
          adPlaying = true;
          applyMute();
        } else if (event?.name === "SDK_GAME_START") {
          adPlaying = false;
          applyMute();
        }
      },
    };
    const js = document.createElement("script");
    js.id = "gamedistribution-jssdk";
    js.src = "https://html5.api.gamedistribution.com/main.min.js";
    document.head.appendChild(js);
    return;
  }
  if (isPlaygama) {
    ready = (async () => {
      try {
        await bridge()?.initialize?.();
        const b = bridge();
        b?.platform?.on?.(b.EVENT_NAME.AUDIO_STATE_CHANGED, (enabled: boolean) => {
          portalMuted = !enabled;
          applyMute();
        });
        b?.platform?.on?.(b.EVENT_NAME.PAUSE_STATE_CHANGED, (paused: boolean) => {
          adPlaying = Boolean(paused);
          applyMute();
        });
        // Saved settings and progress live in the Bridge storage on Playgama.
        await loadBridgeStore();
        reloadUserMuted();
        // The player's language on Playgama (applied unless they or the URL already chose one).
        applyPlatformLang(b?.platform?.language);
      } catch (e) {}
      announceInviteRoom();
    })();
    return;
  }
  ready = (async () => {
    try {
      await sdk()?.init?.();
      sdk()?.game?.loadingStart?.();
      // Saved settings and progress through the SDK's Data module (store.ts), read before the first screen.
      enableCrazyGamesData();
      reloadUserMuted();
      // The player's language on CrazyGames (SDK system info), unless they or the URL already chose one.
      applyPlatformLang(sdk()?.user?.systemInfo?.locale);
      const game = sdk()?.game;
      portalMuted = Boolean(game?.settings?.muteAudio);
      applyMute();
      game?.addSettingsChangeListener?.((settings: any) => {
        portalMuted = Boolean(settings?.muteAudio);
        applyMute();
      });
    } catch (e) {
      // The game must work even if the SDK fails to load.
    }
    announceInviteRoom();
  })();
};

// Playgama and CrazyGames: wait (at most 4 s) for the portal's SDK and the saved settings before the first screen.
export const portalStartupReady = (): Promise<void> => {
  if (!(isPlaygama || isCrazyGames) || !ready) return Promise.resolve();
  return Promise.race([ready.catch(() => undefined), new Promise<void>((r) => setTimeout(r, 4000))]) as Promise<void>;
};

// The first screen is ready to play (CrazyGames measures loading time with this).
let loaded = false;
export const portalLoadingStop = async () => {
  if (!isPortal || loaded) return;
  loaded = true;
  try {
    await ready;
    if (isPlaygama) bridge()?.platform?.sendMessage?.("game_ready");
    else if (isGamePix) {
      gpx()?.lifecycle?.loading?.(100);
      await gpx()?.lifecycle?.loaded?.();
    } else if (isY8) return; // Y8 has no loading signal
    else sdk()?.game?.loadingStop?.();
  } catch (e) {}
};

// A moment of celebration, e.g. the player won the game.
export const portalHappyTime = async () => {
  if (!isCrazyGames && !isGamePix) return;
  try {
    await ready;
    if (isGamePix) {
      await gpx()?.lifecycle?.loaded?.(); // signals need loaded() first (safe to repeat)
      gpx()?.happyMoment?.("game_won");
    } else sdk()?.game?.happytime?.();
  } catch (e) {}
};

// A short ad between games (CrazyGames "midgame" ad). Always resolves, ad or not, so the
// game carries on whatever happens; never waits more than 60 seconds.
// CrazyGames: ads are not allowed during "basic launch" (the first review stage). Once the
// game is through to full launch, set this to true to request a midgame ad between rounds.
const CRAZYGAMES_ADS = false;
// Playgama: an interstitial between games through the Bridge (on from 1 Nov 2026, Sam's call:
// Playgama's certification requires at least one ad type for the catalogue). Only at the logical
// pause after a finished game, when the player chooses to play again; never mid-round, never
// before the player's first game. How often an ad actually shows is left to the Bridge
// (requirement: the game must not time ads itself). Apps and website are unaffected (ads.ts).
export const PLAYGAMA_ADS = true;

// Where an ad break comes from (also Playgama's "ad_break" telemetry): "game_over" is the end of a
// match ("Play again", "Next opponent", the rematch, leaving), "leave_table" is leaving mid-match
// (Playgama only). "round_break" (the pause between rounds of a match) is no longer asked for
// anywhere: since the first-match package (critic issue #102, 4 Nov 2026) every portal's ad comes
// at the END of a match, so a new player's first match runs from deal to result without one.
export type AdBreak = "game_over" | "round_break" | "leave_table";
// GamePix and Y8 (Sam's rules, 3 Nov 2026; moved to the end of the match 4 Nov 2026): exactly one
// interstitial per match, after it (never before the first deal, never mid-hand), started by the
// player's tap, and no rewarded ads. Other portals keep their own rules.
export const ONE_AD_PER_MATCH = ["gamepix", "y8"];
export const portalAdAllowed = (portal: string, at: AdBreak): boolean => {
  if (ONE_AD_PER_MATCH.includes(portal)) return at === "game_over";
  if (portal === "crazygames") return CRAZYGAMES_ADS;
  if (portal === "playgama") return PLAYGAMA_ADS;
  return portal === "gamedistribution";
};
// Y8 (single-player ad rules) and GamePix (no timer-triggered ads): the break with the ad is only
// ever started by the player pressing the next-round button, never by the auto-deal countdown.
export const adNeedsClick = isGamePix || isY8;

export const portalMidgameAd = (at: AdBreak = "game_over"): Promise<void> => {
  if (!isPortal) return Promise.resolve();
  if (!portalAdAllowed(PORTAL, at)) return Promise.resolve();
  portalTrack("ad_break", { at });
  return new Promise<void>((resolve) => {
    let done = false;
    let started = false;
    const finish = () => {
      if (done) return;
      done = true;
      adPlaying = false;
      applyMute();
      resolve();
    };
    setTimeout(finish, 60000);
    (async () => {
      try {
        await ready;
        if (isGamePix) {
          // The SDK calls the pause/resume handlers (mute) itself; resolves ad or no ad.
          const g = gpx();
          if (typeof g?.ads?.interstitial !== "function") return finish();
          await Promise.resolve(g.lifecycle?.loaded?.()).catch(() => undefined);
          started = true;
          await Promise.resolve(g.ads.interstitial()).catch(() => undefined);
          return finish();
        }
        if (isY8) {
          const y = y8Sdk;
          if (typeof y?.showAd !== "function") return finish();
          // Y8: pause and mute inside beforeAd, resume in afterAd; a skipped break (no fill,
          // frequency cap) runs neither, only adBreakDone. An ad blocker runs nothing: carry on.
          Promise.resolve(
            y.showAd({
              type: "next",
              name: "match-end",
              beforeAd: () => {
                started = true;
                adPlaying = true;
                applyMute();
              },
              afterAd: () => {
                adPlaying = false;
                applyMute();
                finish();
              },
              adBreakDone: () => finish(),
            }),
          ).catch(() => finish());
          setTimeout(() => !started && finish(), 5000);
          return;
        }
        if (isGameDistribution) {
          const g = gd();
          if (typeof g?.showAd !== "function") return finish();
          await Promise.resolve(g.showAd("interstitial")).catch(() => undefined);
          return finish();
        }
        if (isPlaygama) {
          const b = bridge();
          if (typeof b?.advertisement?.showInterstitial !== "function") return finish();
          const onState = (state: string) => {
            if (state === "opened") {
              started = true;
              adPlaying = true;
              applyMute();
            } else if (state === "closed" || state === "failed") {
              b.advertisement.off?.(b.EVENT_NAME.INTERSTITIAL_STATE_CHANGED, onState);
              finish();
            }
          };
          b.advertisement.on(b.EVENT_NAME.INTERSTITIAL_STATE_CHANGED, onState);
          b.advertisement.showInterstitial("game_over");
          // No ad available (too soon after the last one): carry on.
          setTimeout(() => !started && finish(), 4000);
          return;
        }
        const ad = sdk()?.ad;
        if (!ad?.requestAd) return finish();
        ad.requestAd("midgame", {
          adStarted: () => {
            adPlaying = true;
            applyMute();
          },
          adFinished: finish,
          adError: finish,
        });
      } catch (e) {
        finish();
      }
    })();
  });
};

let playing = false;
export const portalGameplayStart = async () => {
  if (!isPortal || playing) return;
  playing = true;
  try {
    await ready;
    if (isPlaygama) bridge()?.platform?.sendMessage?.("gameplay_started");
    else if (isGamePix) {
      await gpx()?.lifecycle?.loaded?.(); // gameStart after loaded (safe to repeat)
      gpx()?.lifecycle?.gameStart?.(); // only the first call counts
    } else if (isY8) return; // Y8 has no gameplay signals
    else sdk()?.game?.gameplayStart?.();
  } catch (e) {}
};

export const portalGameplayStop = async () => {
  if (!isPortal || !playing) return;
  playing = false;
  try {
    await ready;
    if (isPlaygama) bridge()?.platform?.sendMessage?.("gameplay_stopped");
    else if (isGamePix || isY8) return; // GamePix: gameStart only; Y8: no gameplay signals
    else sdk()?.game?.gameplayStop?.();
  } catch (e) {}
};

// Playgama (v41): an ad when the player chooses to leave the table, to the home screen or the
// lobby, once a round has been dealt (never before the first deal of a visit: that's the "dealt"
// condition, as the table only offers these exits once the game is on). The Bridge's minimum
// delay decides whether one shows. Other portals, the website and the apps: nothing.
export const portalLeaveTableAd = (dealt: boolean): Promise<void> =>
  isPlaygama && dealt ? portalMidgameAd("leave_table") : Promise.resolve();

// Playgama's telemetry only sees Bridge events (session, ads, gameplay start/stop), not how far a
// player got. These game events go to it too (bridge.analytics.send), so the console shows where
// sessions end: first deal (quick_start), later rounds (start_round), round_over, game_over.
// Bounded names only; data is a flat object of short values. Other builds: nothing.
const BRIDGE_EVENTS = new Set([
  "quick_start",
  "start_round",
  "round_over",
  "game_over",
  "play_again",
  "tutorial_complete",
  "ad_break",
]);
export const portalTrack = (name: string, data?: Record<string, string | number | boolean>) => {
  if (!isPlaygama || !BRIDGE_EVENTS.has(name)) return;
  (ready || Promise.resolve())
    .then(() => {
      const a = bridge()?.analytics;
      if (typeof a?.send === "function") {
        if (data) a.send(name, data);
        else a.send(name);
      }
    })
    .catch(() => undefined);
};
